import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Sun, Loader2, TrendingUp, TrendingDown, Calendar, MessageSquare,
  Briefcase, ArrowRight, ShieldAlert, Sparkles, Hash,
} from "lucide-react";

const fmtINR = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v || 0);

export default function MorningBrief() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/morning-brief").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return (
      <div className="space-y-4">
        <div className="h-12 w-72 bg-white/5 rounded animate-pulse" />
        <div className="h-32 bg-white/5 rounded-2xl animate-pulse" />
        <div className="grid grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  const c = data.counts;

  return (
    <div className="space-y-8" data-testid="morning-brief-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
          <Sun className="h-3.5 w-3.5" /> Today's brief · {new Date(data.generated_at).toLocaleString()}
        </div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">My Morning Brief</h1>
      </header>

      <section className="glass rounded-2xl p-6 lg:p-8 border border-indigo-500/30 bg-gradient-to-br from-indigo-500/[0.08] to-transparent" data-testid="ai-summary">
        <div className="flex items-start gap-3">
          <Sparkles className="h-6 w-6 text-indigo-400 shrink-0 animate-ai-pulse" />
          <p className="text-lg lg:text-xl text-white leading-relaxed">{data.summary}</p>
        </div>
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="brief-counts">
        <CountCard icon={Briefcase} label="Open leads" value={c.open_leads} accent="indigo" link="/dashboard/crm" />
        <CountCard icon={Calendar} label="Going live today" value={c.scheduled_today} accent="emerald" link="/dashboard/calendar" />
        <CountCard icon={TrendingUp} label="Rank moves (7d)" value={c.rank_changes_7d} accent="violet" link="/dashboard/seo" />
        <CountCard icon={MessageSquare} label="Awaiting reply" value={c.inbox_pending} accent="amber" link="/dashboard/inbox"
          sub={c.inbox_escalated > 0 ? `${c.inbox_escalated} escalated` : null} />
      </section>

      <section className="grid lg:grid-cols-2 gap-6">
        <Panel title="Top 5 leads to call today" icon={Briefcase} link="/dashboard/crm" linkLabel="Open CRM" data-testid="brief-leads">
          {(data.top_leads || []).length === 0 ? <Empty msg="No open leads. Try Power Hour to fill the pipe." /> : (
            <ul className="space-y-2">
              {data.top_leads.map((l) => (
                <li key={l.id} className="glass rounded-lg p-3 flex items-center gap-3 text-sm" data-testid={`brief-lead-${l.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{l.name}</div>
                    <div className="text-xs text-white/40 truncate">{l.company || ""} · {l.stage}</div>
                    {l.ai_next_step && <div className="text-xs text-indigo-300 mt-1 line-clamp-2">→ {l.ai_next_step}</div>}
                  </div>
                  {l.deal_value > 0 && <div className="text-xs text-emerald-300 font-mono whitespace-nowrap">{fmtINR(l.deal_value)}</div>}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Going live today" icon={Calendar} link="/dashboard/calendar" linkLabel="Calendar" data-testid="brief-scheduled">
          {(data.scheduled_today || []).length === 0 ? <Empty msg="Nothing scheduled for today." /> : (
            <ul className="space-y-2">
              {data.scheduled_today.slice(0, 8).map((s) => (
                <li key={s.id} className="glass rounded-lg p-3 text-sm" data-testid={`brief-sched-${s.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 text-[10px] uppercase tracking-[0.15em]">{s.platform}</span>
                    <span className="text-xs text-white/40 font-mono">{new Date(s.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                  <div className="text-white/80 line-clamp-2 text-xs">
                    {typeof s.content === "string" ? s.content : (s.content?.post || s.content?.headline || JSON.stringify(s.content).slice(0, 120))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="SEO rank moves (last 7 days)" icon={TrendingUp} link="/dashboard/seo" linkLabel="SEO Studio" data-testid="brief-ranks">
          {(data.rank_changes || []).length === 0 ? <Empty msg="Log positions in SEO Studio → Rank tracker to see trends here." /> : (
            <ul className="space-y-2">
              {data.rank_changes.map((r) => {
                const T = r.delta > 0 ? TrendingUp : TrendingDown;
                const color = r.delta > 0 ? "text-emerald-300" : "text-rose-300";
                return (
                  <li key={r.keyword} className="glass rounded-lg p-3 flex items-center gap-3 text-sm" data-testid={`brief-rank-${r.keyword}`}>
                    <Hash className="h-3 w-3 text-white/40 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{r.keyword}</div>
                      <div className="text-xs text-white/40 truncate font-mono">{r.target_url}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-xl font-black">#{r.position}</div>
                      <div className={`text-xs font-mono inline-flex items-center gap-1 ${color}`}>
                        <T className="h-3 w-3" /> {r.delta > 0 ? `+${r.delta}` : r.delta}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel title="Inbox awaiting reply" icon={MessageSquare} link="/dashboard/inbox" linkLabel="Reply Inbox" data-testid="brief-inbox">
          {c.inbox_escalated > 0 && (
            <div className="glass rounded-lg p-3 border border-rose-500/30 bg-rose-500/[0.06] mb-3 flex items-start gap-2 text-sm">
              <ShieldAlert className="h-4 w-4 text-rose-300 mt-0.5" />
              <span><strong className="text-rose-300">{c.inbox_escalated}</strong> escalated message(s) — human attention required.</span>
            </div>
          )}
          {(data.pending_inbox || []).length === 0 ? <Empty msg="Inbox zero. Beautiful." /> : (
            <ul className="space-y-2">
              {data.pending_inbox.map((m) => (
                <li key={m.id} className="glass rounded-lg p-3 text-sm" data-testid={`brief-inbox-${m.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="px-1.5 py-0.5 rounded bg-white/10 text-white/70 text-[10px] uppercase tracking-[0.15em]">{m.platform}</span>
                    <span className="text-xs text-white/40 truncate max-w-[40%]">{m.author}</span>
                  </div>
                  <div className="text-white/75 line-clamp-2">{m.text}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </section>
    </div>
  );
}

function CountCard({ icon: Icon, label, value, accent, link, sub }) {
  return (
    <Link to={link || "#"} className="glass rounded-xl p-4 tilt-card block" data-testid={`brief-count-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-[0.2em] text-white/40">{label}</span>
        <Icon className={`h-4 w-4 text-${accent}-400`} />
      </div>
      <div className="font-heading text-3xl font-black">{value}</div>
      {sub && <div className="text-xs text-rose-300 mt-1">{sub}</div>}
    </Link>
  );
}

function Panel({ title, icon: Icon, link, linkLabel, children, "data-testid": testid }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-heading font-black flex items-center gap-2">
          <Icon className="h-4 w-4 text-indigo-400" /> {title}
        </div>
        {link && <Link to={link} className="text-xs text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-1">{linkLabel} <ArrowRight className="h-3 w-3" /></Link>}
      </div>
      {children}
    </div>
  );
}

function Empty({ msg }) {
  return <div className="text-sm text-white/40 text-center py-6 border border-dashed border-white/10 rounded-lg">{msg}</div>;
}
